import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { NotebookPen, MapPin, Clock, CalendarDays, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { Spinner } from "../components/common";

const TYPE_LABEL = {
  hike: "Hiking / Trekking", picnic: "Picnic", swimming: "Swimming", museum: "Museum", food: "Food & Dining",
  beach: "Beach", shopping: "Shopping", party: "Party", birthday: "Birthday Party", wedding: "Wedding",
  event: "Event", sightseeing: "Sightseeing", wildlife: "Wildlife", adventure: "Adventure",
  relaxation: "Relaxation", family: "Family", photography: "Photography", other: "Moment",
};

/* Public read-only keepsake view of one shared memoir. */
export default function SharedMemoir() {
  const { token } = useParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/memoirs/shared/${token}`).then((r) => setM(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16 text-center" data-testid="shared-memoir-unavailable">
        <NotebookPen className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
        <h1 className="font-heading font-900 text-2xl mb-2">This shared memoir is unavailable</h1>
        <p className="text-sm text-muted-foreground">The owner may have turned sharing off. Ask them for a fresh link.</p>
      </div>
    );
  }
  if (!m) return <Spinner full />;

  const dateStr = m.date
    ? new Date(`${m.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    : "";
  const typeLabel = m.activity_type === "other" && m.activity_other ? m.activity_other : TYPE_LABEL[m.activity_type] || "Moment";

  return (
    <div className="max-w-2xl mx-auto px-5 lg:px-8 py-10" data-testid="shared-memoir-page">
      <div className="text-xs font-mono uppercase tracking-wider text-indigo-600 mb-2 flex items-center gap-1.5">
        <NotebookPen className="w-4 h-4" /> A memoir shared by {m.owner_name}
      </div>
      <h1 className="font-heading font-900 text-3xl sm:text-4xl mb-2" data-testid="shared-memoir-title">{m.title}</h1>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mb-1">
        {dateStr && <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {dateStr}</span>}
        {m.time && <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {m.time}</span>}
        {m.location && (
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.location)}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-indigo-700 hover:underline" title="Open in Google Maps">
            <MapPin className="w-4 h-4" /> {m.location}
          </a>
        )}
      </div>
      <span className="inline-block text-[11px] font-700 bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded mb-6">{typeLabel}</span>

      {m.notes && <p className="text-base text-zinc-800 whitespace-pre-line leading-relaxed mb-6" data-testid="shared-memoir-notes">{m.notes}</p>}

      {(m.links || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="shared-memoir-links">
          {m.links.map((l) => (
            <a key={l.id || l.url} href={l.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-700 text-indigo-700 border border-indigo-200 bg-indigo-50/60 px-2 py-0.5 hover:bg-indigo-100 transition-colors">
              <ExternalLink className="w-3 h-3" /> {l.title || (() => { try { return new URL(l.url).hostname.replace(/^www\./, ""); } catch { return l.url; } })()}
            </a>
          ))}
        </div>
      )}

      {(m.photos || []).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8" data-testid="shared-memoir-photos">
          {m.photos.map((ph) => (
            <figure key={ph.id}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}`} alt={ph.caption || ""}
                className="w-full aspect-square object-cover border border-zinc-200" />
              {ph.caption && <figcaption className="text-xs text-muted-foreground mt-1">{ph.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}

      <p className="text-sm italic text-indigo-900/70 border-t border-zinc-200 pt-5">Every Moment Has a Story. Keep Yours.</p>
      <p className="text-sm text-muted-foreground mt-2">
        Keep your own moments — hikes, picnics, celebrations and everyday memories —{" "}
        <Link to="/memoirs" className="text-primary font-700 hover:underline" data-testid="shared-memoir-cta">start your memoirs free on TravelerPulse →</Link>
      </p>
    </div>
  );
}
